import { useCallback, useMemo } from "react";
import appLogger from "../../infra/logger/appLogger";
import useApp from "../app/useApp";
import { useAppDispatch, useAppSelector } from "../hook";
import { createGenerationPreset, deleteGenerationPreset, updateGenerationPreset } from "../user/userSettings/userSettingsSlice";
import useUserInfo from "../user/useUserInfo";
import { createModelNameMap, createPresetInput, getAvailablePresets, getModelName, getPresetById, getUnavailableModelIds, isDuplicatePreset, } from "./presetUtils";
const generationPresetsLogger = appLogger.withTag("GenerationPresets");
/**
 * Comprehensive hook for managing generation presets
 * Combines data fetching, computed values, and CRUD operations
 */
export function useGenerationPresets() {
    const dispatch = useAppDispatch();
    const { inferenceModels } = useApp();
    const { permissions } = useUserInfo();
    const generationPresets = useAppSelector((state) => state.userSettings.generation_presets);
    // Computed: Unavailable model IDs based on permissions and existence
    const unavailableModelIds = useMemo(() => getUnavailableModelIds(inferenceModels, permissions, generationPresets), [inferenceModels, permissions, generationPresets]);
    // Computed: Filter presets to only those with available models
    const availablePresets = useMemo(() => getAvailablePresets(generationPresets, inferenceModels, unavailableModelIds), [generationPresets, inferenceModels, unavailableModelIds]);
    // Computed: Model name lookup map
    const modelNameMap = useMemo(() => createModelNameMap(inferenceModels), [inferenceModels]);
    // CRUD: Create a new preset
    const onCreatePreset = useCallback(async (settings, modelName, customName) => {
        // Check for duplicates
        if (isDuplicatePreset(generationPresets, settings)) {
            return null;
        }
        // Create preset input with generated ID and name
        const presetInput = createPresetInput(settings, modelName, customName);
        try {
            const createdPreset = await dispatch(createGenerationPreset(presetInput)).unwrap();
            return createdPreset;
        }
        catch (error) {
            generationPresetsLogger.error("Failed to create preset:", error);
            throw error;
        }
    }, [generationPresets, dispatch]);
    // CRUD: Update an existing preset
    const onUpdatePreset = useCallback(async (presetId, updates) => {
        try {
            const updatedPreset = await dispatch(updateGenerationPreset({ presetId, updates })).unwrap();
            return updatedPreset;
        }
        catch (error) {
            generationPresetsLogger.error("Failed to update preset:", error);
            throw error;
        }
    }, [dispatch]);
    // CRUD: Delete a preset
    const onDeletePreset = useCallback(async (presetId) => {
        try {
            await dispatch(deleteGenerationPreset(presetId)).unwrap();
        }
        catch (error) {
            generationPresetsLogger.error("Failed to delete preset:", error);
            throw error;
        }
    }, [dispatch]);
    // Helper: Get model name by ID
    const getModelNameById = useCallback((modelId) => {
        return getModelName(modelNameMap, modelId);
    }, [modelNameMap]);
    // Helper: Get preset by ID
    const getPreset = useCallback((presetId) => {
        return getPresetById(generationPresets, presetId);
    }, [generationPresets]);
    // Helper: Get preset settings ready to apply
    const getPresetSettings = useCallback((presetId) => {
        const preset = getPresetById(generationPresets, presetId);
        if (!preset)
            return null;
        return {
            modelId: preset.modelId,
            maxNewTokens: preset.maxNewTokens,
            temperature: preset.temperature,
            topP: preset.topP,
            topK: preset.topK,
        };
    }, [generationPresets]);
    return {
        // Raw data
        generationPresets,
        availablePresets,
        unavailableModelIds,
        // CRUD operations
        onCreatePreset,
        onUpdatePreset,
        onDeletePreset,
        // Helper methods
        getModelNameById,
        getPreset,
        getPresetSettings,
    };
}
